import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { CampaignsContext } from "contexts/Campaigns";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { NewCampaignContext } from "contexts/NewCampaign";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Campaign, CampaignBrand, CampaignStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { formatCampaignDate } from "utils/time";

import DeleteCampaignDialog from "../CampaignDetails/DeleteCampaignDialog";
import EditableCampaignDetails from "../CampaignDetails/EditableCampaignDetails";
import UneditableCampaignDetails from "../CampaignDetails/UneditableCampaignDetails";
import { CampaignStatusChip } from "../CampaignStatusChip";
import CampaignBrandCard from "../NewCampaign/BrandSearch/Brands/CampaignBrandCard";
import UncheckBrand from "../NewCampaign/BrandSearch/Brands/CampaignBrandCard/UncheckBrand";
import styles from "./styles";

export default function CampaignDialog({
  isLocal = false,
}: {
  isLocal?: boolean;
}) {
  const theme = useTheme();
  const { excludedBentoBrandIds } = useContext(NewCampaignContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const _isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    _isMobileScreen || (isMediumScreen && discoverTab !== null);
  const navigate = useNavigate();

  const { getCampaign } = useContext(CampaignsContext);
  const { campaignId: id } = useParams();

  const campaignId = Number(id);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignBrands, setCampaignBrands] = useState<CampaignBrand[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadingCampaign, setLoadingCampaign] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [brandsCount, setBrandsCount] = useState<number | undefined>();

  const fetchCampaignBrands = async () => {
    if (page === 1) {
      setLoadingCampaign(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const res = await getCampaign(campaignId, page);
      if (!res) {
        return;
      }
      const { campaign, campaignBrands, brandsCount } = res;
      setCampaign(campaign);
      if (page === 1) {
        setCampaignBrands(campaignBrands);
      } else {
        setCampaignBrands((prev) => [...prev, ...campaignBrands]);
      }
      setPage(page + 1);
      setBrandsCount(brandsCount);
    } finally {
      setLoadingCampaign(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchCampaignBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleClose = () => {
    navigate("..");
  };

  const totalSelectedBrands =
    (brandsCount || 0) - (excludedBentoBrandIds.size || 0);

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="lg"
      onClose={handleClose}
      fullScreen={isMobileScreen}
    >
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
        }}
        onClick={handleClose}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>

      <DialogTitle gap={2}>
        {!isLocal ? (
          <Grid container alignItems="center" gap={1}>
            {campaign && formatCampaignDate(campaign.startDate, false)} to{" "}
            {campaign && formatCampaignDate(campaign.endDate, false)}
            {" | "}
            {campaign?.name}
            {campaign?.status && (
              <CampaignStatusChip
                status={campaign?.status}
                startDate={campaign?.startDate}
              />
            )}
          </Grid>
        ) : (
          "Schedule Emails"
        )}
        {campaign?.status === CampaignStatus.pending_approval && (
          <Box sx={styles.banner}>
            {isLocal ? (
              <>
                🪄 You can schedule these emails later in your{" "}
                <Link component={RouterLink} to={`/${routes.campaignView}`}>
                  Campaigns page.
                </Link>
              </>
            ) : (
              <>
                <strong>🪄 Bento has set up a campaign for you</strong>
                <br />
                All you have to do is review the brands and the templates
                selected, then hit schedule.
              </>
            )}
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={5}>
            <Box>
              {campaign?.status === CampaignStatus.pending_approval ||
              campaign?.status === CampaignStatus.expired ? (
                <>
                  <Typography
                    variant="h6"
                    paragraph
                    sx={{ fontSize: 15, color: grey[700] }}
                  >
                    <strong>{totalSelectedBrands}</strong> Brands Are Selected
                    For This Campaign
                  </Typography>
                </>
              ) : (
                <Typography
                  paragraph
                  variant="h6"
                  sx={{ fontSize: 15, color: grey[700] }}
                >
                  Campaign Brands ({brandsCount})
                </Typography>
              )}
            </Box>
            <Grid item container xs={12}>
              <Grid item container xs={12} gap={1}>
                {campaign?.agentSessionId && (
                  <Box sx={styles.agentSessionBanner}>
                    <p>
                      This campaign was created from your{" "}
                      <Link
                        target="_blank"
                        component={RouterLink}
                        to={`/${routes.home}/${campaign.agentSessionId}`}
                      >
                        conversation here
                      </Link>
                      .
                    </p>
                    <p>
                      Can't see all the brands you scheduled emails for? We
                      might still be creating the brands, finding contacts, and
                      adding them to this campaign.
                    </p>
                  </Box>
                )}
                {campaignBrands.map((brand: CampaignBrand) => (
                  <Grid item xs={12} key={brand.bentoBrand.id}>
                    <CampaignBrandCard
                      brand={brand.bentoBrand}
                      campaignBrand={brand}
                      status={campaign?.status}
                      actions={
                        <>
                          {(campaign?.status ===
                            CampaignStatus.pending_approval ||
                            campaign?.status === CampaignStatus.expired) && (
                            <UncheckBrand
                              campaignBrand={brand}
                              totalBrands={
                                (brandsCount || 0) -
                                (excludedBentoBrandIds.size || 0)
                              }
                              handleOpenDeleteDialog={() => {
                                setOpenDeleteDialog(true);
                              }}
                            />
                          )}
                        </>
                      }
                    />
                  </Grid>
                ))}
                {!!brandsCount &&
                  !loadingCampaign &&
                  campaignBrands.length < brandsCount && (
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      sx={{
                        color: grey[800],
                        backgroundColor: grey[200],
                      }}
                      color="inherit"
                      disableElevation
                      size="small"
                      onClick={() => {
                        fetchCampaignBrands();
                      }}
                      loading={loadingMore}
                    >
                      Load More
                    </LoadingButton>
                  )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} lg={7}>
            {campaign?.status === CampaignStatus.pending_approval ||
            campaign?.status === CampaignStatus.expired ? (
              <EditableCampaignDetails
                campaign={campaign}
                totalSelectedBrands={totalSelectedBrands}
                isLocal={isLocal}
              />
            ) : (
              <UneditableCampaignDetails
                partialCampaign={campaign || undefined}
                campaign={campaign}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {loadingCampaign && (
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: 9999,
          }}
          open={loadingCampaign}
        >
          <CircularProgress />
        </Backdrop>
      )}

      {campaign?.id && (
        <DeleteCampaignDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          campaignId={campaign?.id}
        />
      )}
    </Dialog>
  );
}
